import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que la pantalla de Productos/Servicios le pide a la API (#112), con la
// forma del `datos.ts` de clientes: clave de caché con el taller adentro, una
// consulta por lectura y una función por escritura que devuelve el
// `ProblemDetails` o nada.
//
// Lo propio del catálogo: la exportación y la plantilla bajan un archivo en vez
// de un JSON, y la importación sube uno (#114).

export type Item = components['schemas']['CatalogItemListItem'];
export type FichaItem = components['schemas']['CatalogItemDetail'];
export type Categoria = components['schemas']['CatalogCategoryListItem'];
export type Clase = components['schemas']['CatalogItemKind'];
export type FormatoExportacion = 'xlsx' | 'csv' | 'pdf';
/** La plantilla no se ofrece en PDF: no se puede llenar. */
export type FormatoPlantilla = Exclude<FormatoExportacion, 'pdf'>;
export type RevisionImportacion = components['schemas']['CatalogImportPreview'];
export type ResultadoImportacion = components['schemas']['CatalogImportResult'];

/** 5 MB, el mismo tope de la API (`CatalogImport.MaxBytes`): se avisa antes de subir. */
export const TOPE_IMPORTACION = 5 * 1024 * 1024;

export interface FiltrosCatalogo {
  buscar: string;
  tipo: 'todos' | Clase;
  /** El id de la categoría, o `todas`. */
  categoria: string;
  estado: 'todos' | 'activos' | 'inactivos';
  cursor: string | null;
}

/// El taller de la pestaña (#205): va en toda clave de caché (regla 5 de multitenancy,
/// en el navegador). Ver `sucursales/datos.ts`.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['catalogo', taller] as const,
  lista: (taller: string, filtros: FiltrosCatalogo) => ['catalogo', taller, 'lista', filtros] as const,
  categorias: (taller: string) => ['catalogo', taller, 'categorias'] as const,
  ficha: (taller: string, id: string) => ['catalogo', taller, 'ficha', id] as const,
  // Bajo `catalogo`: guardar, activar o desactivar invalidan esa rama, y el
  // historial se relee solo.
  historial: (taller: string, id: string) => ['catalogo', taller, 'historial', id] as const,
  // Fuera de `catalogo`: guardar invalida esa rama, y volver a pedir el código
  // justo antes de salir del alta sería una consulta para nadie.
  siguienteCodigo: (taller: string, clase: Clase) => ['siguienteCodigoCatalogo', taller, clase] as const,
};

/**
 * Lo que edita el formulario (#113). Los nombres son los del contrato —los
 * errores por campo de la API llegan con esas claves (#76)—, salvo dos que la
 * pantalla escribe distinto de como viajan:
 *
 *   `listPrice`  texto, como se escribe: `45.50`, `1500` (sin las comas que muestra)
 *   `horas`      la duración en horas; viaja como `referenceDurationMinutes`
 */
export interface ValoresItem {
  kind: Clase;
  code: string;
  name: string;
  categoryId: string;
  listPrice: string;
  horas: string;
  externalCode: string;
  aliases: string[];
  isActive: boolean;
}

/**
 * Un número escrito en el formulario, o `null` si está vacío, o `NaN` si no se
 * entiende —y entonces `validar` lo marca—.
 *
 * Lo escribe un `CampoNumero` (#156): dígitos y un punto decimal. Las comas de
 * miles solo se muestran; al formulario llega `1500.00`, sin ellas. `12.` y `.5`
 * valen: es lo que queda si se guarda a medio escribir.
 *
 * Solo dígitos: `Number` sola aceptaría `Infinity`, `1e3` o `0x10`, y el primero
 * viajaría como `null` y borraría el precio sin avisar.
 */
export function numeroDe(texto: string): number | null {
  const limpio = texto.trim();
  if (limpio === '') return null;
  return /^(\d+\.?\d*|\.\d+)$/.test(limpio) ? Number(limpio) : Number.NaN;
}

/** Lo que se manda, en la forma del contrato. */
function cuerpo(v: ValoresItem) {
  const horas = numeroDe(v.horas);
  return {
    code: v.code.trim(),
    name: v.name.trim(),
    ...(v.categoryId && { categoryId: v.categoryId }),
    listPrice: numeroDe(v.listPrice),
    // Los productos no llevan duración; la API los rechazaría si viniera.
    referenceDurationMinutes: v.kind === 'service' && horas !== null ? Math.round(horas * 60) : null,
    externalCode: v.externalCode.trim() || null,
    aliases: v.aliases,
    isActive: v.isActive,
  };
}

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/** Los filtros como los entiende la API: solo lo que tiene valor. */
function consultaDe(filtros: Omit<FiltrosCatalogo, 'cursor'>) {
  return {
    ...(filtros.buscar.trim() && { search: filtros.buscar.trim() }),
    ...(filtros.tipo !== 'todos' && { kind: filtros.tipo }),
    ...(filtros.categoria !== 'todas' && { category: filtros.categoria }),
    ...(filtros.estado !== 'todos' && { isActive: filtros.estado === 'activos' }),
  };
}

export function useCatalogo(filtros: FiltrosCatalogo) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.lista(taller, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/catalog-items', {
        params: {
          query: { ...consultaDe(filtros), ...(filtros.cursor && { cursor: filtros.cursor }) },
        },
      });
      if (error || !data) throw new Error('No se pudo leer el catálogo');
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** Las categorías activas, para el filtro. Son pocas y cambian poco. */
export function useCategorias() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.categorias(taller),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/catalog-categories');
      if (error || !data) throw new Error('No se pudieron leer las categorías');
      return data.items;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** La ficha para editar o duplicar. `null` si no existe, está eliminada o es de otro taller. */
export function useFichaItem(id: string | undefined) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== undefined,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/catalog-items/{id}', {
        params: { path: { id: id ?? '' } },
      });
      // El 404 no es un fallo: el ítem no existe para este taller. La pantalla lo dice distinto.
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer el producto o servicio');
      return data;
    },
  });
}

/**
 * El historial del ítem (#181): quién lo creó y quién le cambió qué, de qué valor
 * a cuál, lo más nuevo primero. Pide lo mismo que la ficha, `catalog.edit`.
 *
 * Solo se pide con el panel abierto: no al abrir la ficha (#193).
 */
export function useHistorialItem(id: string, abierto: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.historial(taller, id),
    enabled: abierto,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/catalog-items/{id}/history', { params: { path: { id } } });
      if (error || !data) throw new Error('No se pudo leer el historial del producto o servicio');
      return data;
    },
  });
}

/**
 * El código que tendría el próximo ítem de esa clase, para mostrarlo ya puesto.
 * No lo reserva: el formulario lo manda vacío si nadie lo tocó (ver `ICodeSequences.PeekAsync`).
 */
export function useSiguienteCodigo(clase: Clase, habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.siguienteCodigo(taller, clase),
    enabled: habilitado,
    queryFn: () => pedirSiguienteCodigo(clase),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Otro código propuesto, o vacío si no se pudo saber: el alta sigue sin él. */
export async function pedirSiguienteCodigo(clase: Clase): Promise<string> {
  try {
    const { data } = await api.GET('/api/catalog-items/next-code', { params: { query: { kind: clase } } });
    return data?.code ?? '';
  } catch {
    return '';
  }
}

/** Las escrituras de la pantalla. Cada una refresca el catálogo del taller al terminar bien. */
export function useCambiosItem() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function crear(v: ValoresItem, codigoPropuesto: string): Promise<ProblemaApi | undefined> {
    const datos = cuerpo(v);
    // El propuesto que nadie tocó se manda vacío y lo asigna el servidor: mandar
    // el que se mostró haría chocar dos altas simultáneas.
    const { error } = await api.POST('/api/catalog-items', {
      body: { ...datos, kind: v.kind, code: datos.code === codigoPropuesto ? null : datos.code },
    });
    if (error) return error as ProblemaApi;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return undefined;
  }

  async function editar(id: string, versionItem: number, v: ValoresItem): Promise<ProblemaApi | undefined> {
    const { error } = await api.PUT('/api/catalog-items/{id}', {
      params: { path: { id } },
      body: { ...cuerpo(v), version: versionItem },
    });
    if (error) return error as ProblemaApi;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return undefined;
  }

  /** El alta al vuelo del popup «Nueva categoría». Devuelve la creada para dejarla elegida. */
  async function crearCategoria(
    nombre: string,
  ): Promise<{ categoria?: Pick<Categoria, 'id' | 'name'>; problema?: ProblemaApi }> {
    const { data, error } = await api.POST('/api/catalog-categories', { body: { name: nombre.trim() } });
    if (error || !data) return { problema: error as ProblemaApi };
    await cache.invalidateQueries({ queryKey: claves.categorias(taller) });
    return { categoria: data };
  }

  async function cambiarEstado(id: string, versionItem: number, activo: boolean): Promise<ProblemaApi | undefined> {
    const { error } = await api.PUT('/api/catalog-items/{id}/status', {
      params: { path: { id } },
      body: { version: versionItem, isActive: activo },
    });
    if (error) return error as ProblemaApi;
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return undefined;
  }

  /**
   * Importa las filas válidas del archivo que se revisó (#114). La API lo vuelve
   * a revisar contra la base de este momento, así que puede devolver problemas
   * que la revisión no tenía.
   */
  async function importar(archivo: File): Promise<{ resultado?: ResultadoImportacion; problema?: ProblemaApi }> {
    const { data, error } = await api.POST('/api/catalog-items/import', conArchivo(archivo));
    if (error || !data) return { problema: error as ProblemaApi };
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return { resultado: data };
  }

  /**
   * Baja lógica, solo de lo que no aparece en ninguna orden: si aparece, la API
   * responde 409 `catalog_item.en_ordenes`. La ventana lo sabe antes por la
   * ficha; el 409 cubre la orden que se confirmó mientras la ventana estaba abierta.
   */
  async function eliminar(id: string): Promise<ProblemaApi | undefined> {
    const { error } = await api.DELETE('/api/catalog-items/{id}', { params: { path: { id } } });
    // Con el cuerpo vacío, `error` llega como `""`: falso, pero es un error.
    if (error !== undefined) return (typeof error === 'object' && error ? error : {}) as ProblemaApi;
    // Todo el catálogo menos la ficha que se acaba de eliminar y su historial:
    // pedirlos de nuevo daría un 404, y la pantalla abierta —la ficha, o la
    // ventana sobre el listado— diría «no existe» justo mientras se sale. Se
    // queda con lo último que sabía hasta que deja de usarse.
    const eliminadas = [claves.ficha(taller, id), claves.historial(taller, id)];
    await cache.invalidateQueries({
      queryKey: claves.todo(taller),
      predicate: (consulta) =>
        !eliminadas.some((clave) => clave.every((parte, i) => consulta.queryKey[i] === parte)),
    });
    return undefined;
  }

  return { crear, editar, crearCategoria, cambiarEstado, importar, eliminar };
}

/**
 * El cuerpo `multipart` con el archivo. El contrato tipa el archivo como `string`
 * porque así describe OpenAPI a un `IFormFile`; lo que viaja es el `File` (ver
 * `adjuntos/datos.ts`).
 */
function conArchivo(archivo: File) {
  return {
    body: { file: archivo as unknown as string },
    bodySerializer: () => {
      const formulario = new FormData();
      formulario.append('file', archivo, archivo.name);
      return formulario;
    },
  };
}

/** Revisa el archivo sin guardar nada: el paso 1 de importar. */
export async function revisarImportacion(
  archivo: File,
): Promise<{ revision?: RevisionImportacion; problema?: ProblemaApi }> {
  const { data, error } = await api.POST('/api/catalog-items/import/preview', conArchivo(archivo));
  if (error || !data) return { problema: error as ProblemaApi };
  return { revision: data };
}

/** Baja la plantilla vacía, solo con el encabezado. */
export async function bajarPlantilla(formato: FormatoPlantilla) {
  const { data, error, response } = await api.GET('/api/catalog-items/import-template', {
    params: { query: { format: formato } },
    parseAs: 'blob',
  });
  if (error || !data) throw new Error('No se pudo bajar la plantilla');
  guardarArchivo(data, response, `plantilla-catalogo.${formato}`);
}

/**
 * Baja el archivo de la exportación con los filtros de la pantalla (#111).
 *
 * `incluirInactivos` solo cuenta con el estado en «Todos»: con «Activos» o
 * «Inactivos», el filtro manda y la API ignora la casilla.
 */
export async function exportar(
  formato: FormatoExportacion,
  filtros: Omit<FiltrosCatalogo, 'cursor'>,
  incluirInactivos: boolean,
): Promise<ProblemaApi | undefined> {
  const { data, error, response } = await api.GET('/api/catalog-items/export', {
    params: {
      query: {
        format: formato,
        ...consultaDe(filtros),
        ...(filtros.estado === 'todos' && incluirInactivos && { includeInactive: true }),
      },
    },
    parseAs: 'blob',
  });
  // Con el cuerpo vacío, openapi-fetch deja `error` en `""`, que es falso: sin
  // el objeto, la ventana lo tomaría como éxito y cerraría sin archivo.
  if (error !== undefined || !data) return (typeof error === 'object' && error ? error : {}) as ProblemaApi;
  guardarArchivo(data, response, `catalogo.${formato}`);
  return undefined;
}

/** Entrega el archivo al navegador con el nombre que puso la API (`catalogo-2026-10-04.xlsx`), o uno genérico. */
function guardarArchivo(datos: Blob, response: Response, alterno: string) {
  const disposicion = response.headers.get('Content-Disposition') ?? '';
  const nombre =
    /filename\*=UTF-8''([^;]+)/i.exec(disposicion)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposicion)?.[1] ?? alterno;

  const enlace = document.createElement('a');
  enlace.href = URL.createObjectURL(datos);
  enlace.download = decodeURIComponent(nombre);
  enlace.click();
  URL.revokeObjectURL(enlace.href);
}
